<script setup lang="ts">
import { computed, inject } from "vue";

import { cardActions } from "@/action/cardActions";
import type { Action } from "@/action/types";
import { cardMethodsKey } from "@/components/StickyNote/injectKeys";
import { boardKey } from "@/components/board/injectKeys";
import { emitStickyChanged } from "@/composables/useEventBus";
import type { BoardCard } from "@/model/card";
import { useBoardStore } from "@/store/board";
import { hideCard } from "@/store/helpers/mark";
import { useLinkStore } from "@/store/link";
import { useServerSettingsStore } from "@/store/serverSettings";
import { injectStrict } from "@/utils/vue";

import DeleteButton from "./internal/DeleteButton.vue";
import RemoveButton from "./internal/RemoveButton.vue";
import UnplanButton from "./internal/UnplanButton.vue";

const props = defineProps<{ cards: BoardCard[] }>();

const emit = defineEmits<{
  delete: [cards: BoardCard[]];
  remove: [cards: BoardCard[]];
}>();

const board = injectStrict(boardKey);
const cardMethods = inject(cardMethodsKey, null);

// Server setting: skip confirmation modal for single non-ALM delete with no text
const skipConfirm = computed(
  () =>
    props.cards.length === 1 &&
    (!useServerSettingsStore().confirmDelete || !props.cards[0]?.data.text),
);

async function removeCards(cards: BoardCard[]) {
  await dispatch(cards, cardActions.remove);
  emit("remove", cards);
}

async function deleteCards(cards: BoardCard[]) {
  await dispatch(cards, cardActions.delete);
  emit("delete", cards);
}

async function dispatch(targets: BoardCard[], action: Action<[string, string]>) {
  useBoardStore().setActiveCardId(null);
  await cardMethods?.animate?.("deleting");

  for (const card of targets) {
    hideCard(useBoardStore().currentBoard().cards[card.data.id]);
    useLinkStore().removeCardLinkedMarks(card.data.id);
    emitStickyChanged(card.data.id);
    action("action-menu", card.data.id, board.value.id);
  }
}
</script>

<template>
  <!-- Right-side ordering: unplan → remove → delete. Each button decides
       its own visibility (incl. the disabled state for delete). -->
  <UnplanButton :cards @unplan="(t) => dispatch(t, cardActions.unplan)" />
  <RemoveButton :cards @remove="removeCards" />
  <DeleteButton :cards :skip-confirm @delete="deleteCards" />
</template>
